// packages/api/src/PublicSignupOrderService.ts
//
// ROADMAP A2h #149 — the PUBLIC sign-up cart: a recognized guest pays once for priced sign-up list items picked across
// the site's blocks and pages. reserve (hold the places) → pay (a Stripe PaymentIntent) → finalize (the server asks
// Stripe and turns the lines into sign-ups) — or release (the guest left before paying).
//
// ⚠ Notice what a line does NOT carry: no price, no currency, no amount. The server reads them from the item — a field
// the browser could send is a field the guest controls, and one of them would decide what a sign-up costs.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

export interface PublicSignupOrderLineInput {
  itemId: string;
  /** The party guest it is for — their guest code (`PublicRsvpMineGuest.ref`). */
  guestRef: string;
  quantity: number;
  note?: string | null;
}

export interface PublicSignupOrderReserveInput {
  url: string;
  visitorId: string;
  lines: PublicSignupOrderLineInput[];
  /** The cart changed after a hold: release that hold (this browser's) in the same transaction. */
  replaceOrderId?: string | null;
}

export interface PublicSignupOrderLine {
  id: string;
  itemId: string;
  itemName?: string | null;
  guestRef?: string | null;
  guestName?: string | null;
  quantity: number;
  note?: string | null;
  unitPrice: number;
  amount: number;
}

export interface PublicSignupOrder {
  id: string;
  /** reserved | pending_payment | paid | canceled | refunded */
  status: string;
  reservedUntil?: string | null;
  currencyCode?: string | null;
  subtotal: number;
  /** Added on top for the guest (fees the host passes on); 0 when the host absorbs them. */
  fees: number;
  /** Each fee on its own: our service fee and the card processing fee, as far as the guest pays them. */
  serviceFeeToGuest?: number | null;
  processorFeeToGuest?: number | null;
  total: number;
  isTest?: boolean | null;
  /** Where the receipt goes, masked. Null = no email on file, so no receipt email. */
  receiptEmail?: string | null;
  /** Stripe is still processing the card — the order is finished when it clears. */
  processing?: boolean | null;
  lines: PublicSignupOrderLine[];
}

export interface PublicSignupOrderPayment {
  orderId: string;
  clientSecret: string;
  publishableKey: string;
  /** The host's connected account (a direct charge); null on a test-mode event. */
  connectedAccountId: string | null;
  amount: number;
  currencyCode?: string | null;
}

/** The order statuses (mirror of the API's SIGNUP_ORDER_STATUS). */
export const SIGNUP_ORDER_STATUS = {
  RESERVED: "reserved",
  PENDING_PAYMENT: "pending_payment",
  PAID: "paid",
  CANCELED: "canceled",
  REFUNDED: "refunded",
} as const;

const ORDER_FIELDS = `
  id status reservedUntil currencyCode subtotal fees serviceFeeToGuest processorFeeToGuest total isTest receiptEmail processing
  lines { id itemId itemName guestRef guestName quantity note unitPrice amount }
`;

const RESERVE_MUTATION = gql`
  mutation PublicSignupOrderReserve($params: PublicSignupOrderReserveInput) {
    publicSignupOrderReserve(params: $params) {
      code
      errors { name errors }
      data { ${ORDER_FIELDS} }
    }
  }
`;

const PAY_MUTATION = gql`
  mutation PublicSignupOrderPay($params: PublicSignupOrderInput) {
    publicSignupOrderPay(params: $params) {
      code
      errors { name errors }
      data { orderId clientSecret publishableKey connectedAccountId amount currencyCode }
    }
  }
`;

const FINALIZE_MUTATION = gql`
  mutation PublicSignupOrderFinalize($params: PublicSignupOrderInput) {
    publicSignupOrderFinalize(params: $params) {
      code
      errors { name errors }
      data { ${ORDER_FIELDS} }
    }
  }
`;

const RELEASE_MUTATION = gql`
  mutation PublicSignupOrderRelease($params: PublicSignupOrderInput) {
    publicSignupOrderRelease(params: $params) {
      code
      errors { name errors }
      data { orderId released }
    }
  }
`;

export class PublicSignupOrderService {
  /** Hold the cart's items. An item's refusal is keyed `item:<itemId>`; anything else is generic. */
  static async reserve(params: PublicSignupOrderReserveInput, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(RESERVE_MUTATION, { params }, options);
  }

  /** Start (or resume) the card payment for a held order — what Stripe.js needs. */
  static async pay(params: { url: string; visitorId: string; orderId: string }, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(PAY_MUTATION, { params }, options);
  }

  /**
   * The card form succeeded: ask the server to verify with Stripe and finish the order. ⚠ Sends the ORDER id only — the
   * server reads the PaymentIntent from its own row; this is a nudge to go and look, never a claim that money arrived.
   */
  static async finalize(params: { url: string; visitorId: string; orderId: string }, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(FINALIZE_MUTATION, { params }, options);
  }

  /** The guest left the checkout before paying: give the places back now (a hold with a card form open lapses on its own). */
  static async release(params: { url: string; visitorId: string; orderId: string }, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(RELEASE_MUTATION, { params }, options);
  }
}
